"use client";

import { ArrowLeft, ArrowRight, Clock, Tag, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: any;

function formatDate(dateStr?: string) {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function getInitials(name?: string) {
  if (!name) return "V";
  return name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function estimateReadTime(content?: string) {
  if (!content) return 1;
  const text = content.replace(/<[^>]*>/g, " ");
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 220));
}

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl">
        <div className="mb-8 h-4 w-32 animate-pulse rounded-md bg-muted" />
        <div className="mb-6 h-12 w-full animate-pulse rounded-md bg-muted" />
        <div className="mb-10 h-6 w-2/3 animate-pulse rounded-md bg-muted" />
        <div className="mb-10 aspect-[16/9] w-full animate-pulse rounded-2xl bg-muted" />
        <div className="space-y-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} data-index={i} className="h-4 w-full animate-pulse rounded bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (error || posts.length === 0) {
    return (
      <div className="mx-auto max-w-4xl text-center">
        <p className="text-lg text-muted-foreground">Post not found.</p>
        <Link to="/HomePage" className="mt-4 inline-block">
          <Button variant="outline">Return Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const readTime = estimateReadTime(post.content);

  return (
    <article className="mx-auto max-w-4xl">
      <div className="mb-8">
        <Link
          to="/HomePage"
          className="inline-flex items-center gap-2 text-sm font-medium text-foreground/70 transition-colors hover:text-[color:var(--primary)]"
        >
          <ArrowLeft className="size-4" />
          <span>Back to home</span>
        </Link>
      </div>

      {post.categories && post.categories.length > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-2">
          {post.categories.map((cat, i) => (
            <Link
              key={cat.id}
              data-index={i}
              to={cat.link || "#"}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[color:var(--accent)] px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[color:var(--accent-foreground)]"
            >
              <Tag className="size-3" />
              {cat.name}
            </Link>
          ))}
        </div>
      )}

      <h1 className="font-default text-4xl font-bold leading-tight tracking-tight text-foreground md:text-5xl lg:text-6xl">
        {post.title}
      </h1>

      {post.excerpt && (
        <div
          className="mt-6 text-lg leading-relaxed text-foreground/70 md:text-xl"
          dangerouslySetInnerHTML={{ __html: post.excerpt }}
        />
      )}

      <div className="mt-8 flex flex-wrap items-center gap-6 border-y border-border py-5">
        {post.author && (
          <div className="flex items-center gap-3">
            <Avatar className="size-10">
              {post.author.avatar && (
                <AvatarImage
                  src={post.author.avatar["96"] || post.author.avatar["48"]}
                  alt={post.author.name}
                />
              )}
              <AvatarFallback className="bg-[color:var(--primary)] text-[color:var(--primary-foreground)] text-xs font-semibold">
                {getInitials(post.author.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <span className="text-xs uppercase tracking-wider text-foreground/60">Author</span>
              <span className="text-sm font-semibold text-foreground">{post.author.name}</span>
            </div>
          </div>
        )}
        <div className="hidden h-10 sm:block">
          <Separator orientation="vertical" />
        </div>
        <div className="flex items-center gap-2 text-sm text-foreground/70">
          <Clock className="size-4 text-[color:var(--primary)]" />
          <span>{readTime} min read</span>
        </div>
      </div>

      {post.featuredImage && (
        <div className="mt-10 overflow-hidden rounded-2xl border border-border shadow-lg">
          <img
            src={post.featuredImage}
            alt={post.title}
            loading="lazy"
            decoding="async"
            className="aspect-[16/9] w-full object-cover"
          />
        </div>
      )}

      {post.content && (
        <div
          className="prose prose-lg mt-12 max-w-none text-foreground/90 [&_a]:text-[color:var(--primary)] [&_a]:underline [&_a:hover]:text-[color:var(--secondary)] [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:font-default [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:text-foreground [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:font-default [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:text-foreground [&_p]:mb-5 [&_p]:leading-relaxed [&_ul]:mb-5 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:mb-5 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-2 [&_blockquote]:my-6 [&_blockquote]:border-l-4 [&_blockquote]:border-[color:var(--secondary)] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-foreground/80 [&_img]:my-6 [&_img]:rounded-xl [&_code]:rounded [&_code]:bg-[color:var(--muted)] [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-sm"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 flex flex-wrap items-center gap-2 border-t border-border pt-8">
          <span className="mr-2 text-sm font-semibold text-foreground">Tags:</span>
          {post.tags.map((tag, i) => (
            <Link
              key={tag.id}
              data-index={i}
              to={tag.link || "#"}
              className="rounded-md border border-border bg-[color:var(--muted)] px-3 py-1 text-xs font-medium text-foreground/80 transition-colors hover:border-[color:var(--primary)] hover:text-[color:var(--primary)]"
            >
              #{tag.name}
            </Link>
          ))}
        </div>
      )}

      {post.author && (
        <div className="mt-12 flex flex-col items-start gap-5 rounded-2xl border border-border bg-[color:var(--card)] p-6 sm:flex-row sm:items-center sm:p-8">
          <Avatar className="size-20 shrink-0">
            {post.author.avatar && (
              <AvatarImage
                src={post.author.avatar["96"] || post.author.avatar["48"]}
                alt={post.author.name}
              />
            )}
            <AvatarFallback className="bg-[color:var(--primary)] text-[color:var(--primary-foreground)] text-xl font-bold">
              {getInitials(post.author.name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-foreground/60">
              <User className="size-3" />
              <span>Founder</span>
            </div>
            <h3 className="mt-1 font-default text-xl font-bold text-foreground">
              {post.author.name}
            </h3>
            <p className="mt-2 text-sm text-foreground/70">
              Victoria Light Foundation founder, dedicated to making the world a better place through love and light.
            </p>
          </div>
        </div>
      )}
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            data-index={i}
            className="h-72 animate-pulse rounded-2xl bg-muted"
          />
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <p className="text-sm text-foreground/60">No other updates found.</p>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {posts.slice(0, 3).map((p, i) => (
        <Link
          key={p.id}
          data-index={i}
          to={p.link || "#"}
          className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-[color:var(--card)] transition-all hover:-translate-y-1 hover:border-[color:var(--primary)] hover:shadow-lg"
        >
          {p.featuredImage ? (
            <div className="aspect-[16/10] overflow-hidden bg-[color:var(--muted)]">
              <img
                src={p.featuredImage}
                alt={p.title}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
          ) : (
            <div className="aspect-[16/10] bg-gradient-to-br from-[color:var(--primary)] to-[color:var(--secondary)]" />
          )}
          <div className="flex flex-1 flex-col p-6">
            {p.categories && p.categories.length > 0 && (
              <span className="mb-3 inline-block w-fit rounded-lg bg-[color:var(--accent)] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--accent-foreground)]">
                {p.categories[0].name}
              </span>
            )}
            <h3 className="font-default text-lg font-semibold leading-snug text-foreground transition-colors group-hover:text-[color:var(--primary)]">
              {p.title}
            </h3>
            {p.excerpt && (
              <div
                className="mt-2 line-clamp-2 text-sm text-foreground/70"
                dangerouslySetInnerHTML={{ __html: p.excerpt }}
              />
            )}
            <div className="mt-4 flex items-center justify-between text-xs text-foreground/60">
              {p.date && <span>{formatDate(p.date)}</span>}
              <span className="inline-flex items-center gap-1 font-semibold text-[color:var(--primary)]">
                Read
                <ArrowRight className="size-3 transition-transform group-hover:translate-x-1" />
              </span>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

export default function Article() {
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : { post_type: "post" };
  const wp_query = new WP_Query(currentWPQueryParams);

  const relatedQuery = new WP_Query({
    post_type: currentWPQueryParams?.post_type || "post",
    per_page: 3,
    orderby: "date",
    order: "DESC",
  });

  return (
    <section
      data-section-id="31945"
      id="single-post-template-section"
      className="relative w-full bg-background py-16 md:py-24"
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <PostsProvider wp_query={wp_query}>
          <SinglePostConsumer />
        </PostsProvider>

        <div className="mx-auto mt-24 max-w-6xl">
          <div className="mb-10 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <span className="inline-block rounded-lg bg-[color:var(--primary)] px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[color:var(--primary-foreground)]">
                Updates
              </span>
              <h2 className="mt-4 font-default text-3xl font-bold tracking-tight text-foreground md:text-4xl">
                Recent posts
              </h2>
              <p className="mt-2 text-foreground/70">
                Latest news from Victoria Light.
              </p>
            </div>
            <Link to="/HomePage">
              <Button
                variant="outline"
                className="border-[color:var(--primary)] text-[color:var(--primary)] hover:bg-[color:var(--primary)] hover:text-[color:var(--primary-foreground)]"
              >
                View all posts
                <ArrowRight className="size-4" />
              </Button>
            </Link>
          </div>

          <PostsProvider wp_query={relatedQuery}>
            <RelatedPostsConsumer />
          </PostsProvider>
        </div>
      </div>
    </section>
  );
}